import "../Styles/tailwind.css";
import React, { useState, useEffect } from "react";
import TopNav from "../layouts/TopNav";
import { AiOutlineShareAlt } from "react-icons/ai";
import { useNavigate, useLocation } from "react-router-dom";
import html2pdf from "html2pdf.js";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import api from "../util/axios";
const numberToWords = require("number-to-words");

type Customer = {
  _id: string;
  businessName: string;
  email?: string;
};

type Requisition = {
  _id: string;
  approvalNumber: string;
  availableToBook: number;
  customer: Customer;
  itemId: string;
  itemName: string;
  quantity: number;
  quantityToLoad: number;
  quantityLoaded: number;
  totalCost: number;
  unitPrice: number;
};

type DailyAllocation = {
  destination: string;
  exDepotPrice: number;
  license: {
    _id: string;
    licenseNumber: string;
    licenseOwner: string;
    destination: string;
  } | null;
}

type LoadingSlip = {
  _id: string;
  requisitions: Requisition[];
  truckNumber: string;
  loadingNumber: string;
  supplyLocation: string;
  destination: string;
  loader: string;
  ullager: string;
  loadingTime: {
    started?: string;
    completed?: string;
  };
  meterBefore: string;
  meterAfter: string;
  compartmentNo: {
    first: string;
    second: string;
    third: string;
    fourth: string;
    fifth: string;
    sixth: string;
    seventh: string;
  };
  ullage: {
    first: string;
    second: string;
    third: string;
    fourth: string;
    fifth: string;
    sixth: string;
    seventh: string;
  };
  transporter: string;
  driver: string;
  staff: {
    name: string;
  };
  createdAt: string;
  invoiced: boolean;
};

type InvoiceData = {
  _id: string;
  invoiceNumber: string;
  loadingSlip: LoadingSlip;
  updatedAt: string;
  dailyAllocation?: DailyAllocation | null;
  meterTicket: string;
};

type Business = {
  _id: string;
  logo: string;
  businessName: string;
  email: string;
  lowStock: string;
  inventoryCategories: string[];
  phone: string;
  emailNotifications: string;
  businessCategory: string;
  location: {
    address: string;
    city: string;
    state: string;
    country: string;
  };
};

const Invoice = () => {
  const location = useLocation();
  const datas = location.state as InvoiceData;
  const navigate = useNavigate();
  // console.log("datas:", datas);

  const [invoiceGenerated, setInvoiceGenerated] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("alausabashir@gmail.com");
  const [printCount, setPrintCount] = useState(1);
  const [getBusiness, setGetBusiness] = useState<Business>({
    _id: "",
    logo: "",
    businessName: "",
    email: "",
    lowStock: "",
    inventoryCategories: [],
    phone: "",
    emailNotifications: "",
    businessCategory: "",
    location: {
      address: "",
      city: "",
      state: "",
      country: "",
    },
  });
  const token = window.localStorage.getItem("token");

  function queryBusiness() {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    return api
      .get("/settings", { headers })
      .then((res) => {
        setGetBusiness(res.data.data);
        // console.log("business data to use:", res.data.data);
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  }

  useEffect(() => {
    if (datas?.loadingSlip) {
      const invoiceGeneratedFlag = localStorage.getItem(`invoiceGenerated_${datas.loadingSlip._id}`);
      if (invoiceGeneratedFlag) {
        setInvoiceGenerated(true);
      }
      const printCount = localStorage.getItem(`printCount_${datas.loadingSlip._id}`);
      if (printCount) {
        setPrintCount(parseInt(printCount, 10));
      }
    }
    queryBusiness();
  }, [datas]);

  if (!datas || !datas.loadingSlip) {
    return <div>Error: No data available</div>;
  }

  const requisitionData = datas.loadingSlip.requisitions;
  const isMerged = requisitionData.length > 1;

  const generatePDF = async () => {
    if (invoiceGenerated) {
      Swal.fire({
        title: "Warning!",
        text: `This invoice has been generated before`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "orange",
        confirmButtonText: "Print Again",
      }).then((result) => {
        if (result.isConfirmed) {
          createPDF();
        }
      });
    } else {
      createPDF();
      localStorage.setItem(`invoiceGenerated_${datas.loadingSlip._id}`, "true");
      setInvoiceGenerated(true);
    }
    const newPrintCount = printCount + 1;
    setPrintCount(newPrintCount);
    localStorage.setItem(`printCount_${datas.loadingSlip._id}`, newPrintCount.toString());
  };

  const createPDF = (): void => {
    const slipElement = document.querySelector(".invoice") as HTMLElement;

    if (slipElement) {
      const pdfOptions = {
        margin: 5,
        filename: "invoice.pdf",
        image: { type: "png" },
        html2canvas: {
          scale: 8,
          useCORS: true,
          dpi: 300,
          letterRendering: true,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait", compress: false },
      };

      html2pdf()
        .from(slipElement)
        .set(pdfOptions)
        .toPdf()
        .get("pdf")
        .then((pdf: any) => {
          const pdfUrl = pdf.output("bloburl");
          const printWindow = window.open(pdfUrl, "_blank");

          if (printWindow) {
            printWindow.onload = () => {
              printWindow.print();
            }
          } else {
            console.error("Unable to open the PDF in a new window for printing.");
          }
        })
        .catch((err: Error) => console.error("PDF generation error:", err));
    } else {
      console.error("Invoice element not found");
    }
  };

  const sendEmail = async () => {
    const slipElement = document.querySelector(".invoice");

    if (slipElement) {
      try {
        const pdfOptions = {
          margin: 2,
          filename: "invoice.pdf",
          image: { type: "png" },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait",
            compress: true,
          },
        };
        const pdfBlob = await html2pdf().from(slipElement).set(pdfOptions).output('blob');
        const formData = new FormData();
        formData.append("invoice", pdfBlob, 'invoice.pdf');

        await api.post(
          `/loading-slip/invoice/send`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
          .then((response) => {
            Swal.fire({
              title: "Success!",
              text: "Invoice has been shared via email.",
              icon: "success",
              showCancelButton: false,
              confirmButtonColor: "green",
              confirmButtonText: "OK",
            });
          })
      } catch (error) {
        Swal.fire({
          title: "Error!",
          text: `Error sharing invoice via email: ${error}`,
          icon: "error",
          showCancelButton: false,
          confirmButtonColor: "red",
          confirmButtonText: "Try Again",
        });
      }
    }
  };

  const totalQuantityToLoad = requisitionData.reduce(
    (total: number, requisition: Requisition) => total + requisition.quantityLoaded,
    0
  );
  const totalAmount = requisitionData.reduce(
    (total: number, requisition: Requisition) => total + requisition.totalCost,
    0
  );

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString || isNaN(new Date(dateString).getTime())) {
      return "N/A";
    }
    return new Date(dateString).toLocaleString();
  };

  const formattedStartDate = formatDate(datas.loadingSlip.loadingTime?.started);
  const formattedEndDate = formatDate(datas.loadingSlip.loadingTime?.completed);

  const totalAmountInWords = numberToWords.toWords(totalAmount);
  const updatedAt = datas.updatedAt;
  const updatedAtDateOnly = updatedAt.split("T")[0];
  const createdAt = datas.loadingSlip.createdAt;
  const createAtDateOnly = createdAt.split("T")[0];

  const approvalNumbers = requisitionData
    .map((req: Requisition) => req.approvalNumber)
    .join(" / ");
  const unitPrice = requisitionData.every((req: Requisition) => req.unitPrice === requisitionData[0].unitPrice)
    ? requisitionData[0].unitPrice
    : "Multiple";

  return (
    <div className="board">
      <ToastContainer />
      <TopNav title="Invoice" />
      <div className="border border-black p-4 w-[90%] font-sans invoice my-4 mx-auto relative uppercase">
        <style>
          {`
            .invoice {
              font-family: "Arial, Helvetica, sans-serif !important;
              font-size: 12px;
              font-weight: 700;
              line-height: 1.4 !important;
              -webkit-font-smoothing: antialiased !important;
              -moz-osx-font-smoothing: grayscale !important;
              color: #000000 !important;
              background-color: #ffffff !important;
            }

            .invoice * {
              font-family: inherit !important;
              font-weight: 700;
            }

            .invoice p, .invoice th, .invoice td {
              text-rendering: optimizeLegibility !important;
            }
            .merged-badge {
              background-color: #e0e0e0;
              padding: 2px 5px;
              border-radius: 3px;
              margin-left: 5px;
            }
          `}
        </style>
        <div className="flex flex-col items-center">
          <span className="text-black text-lg font-bold mb-0 pb-0">First Royal Oil Nigeria Limited®</span>
          <span className="text-black text-md font-bold mb-0 pb-0">Ijegun-Egba, Satellite Town, Lagos</span>
          <span className="text-black text-lg font-bold mb-0 pb-0">08187349507</span>
          <span className="text-black text-sm font-bold mb-0 pb-0">Info@firstroyaloilng.com</span>
          <span className="text-black text-sm font-bold mb-0 pb-0">firstroyaloilng.com</span>
          <span className="border-b border-black p-0.5 text-black text-lg font-bold mb-0 pb-0">CUSTOMER INVOICE</span>
        </div>

        <div className="flex items-center">
          <div className="w-[48%]">
            <p className="flex text-black text-sm font-bold gap-2 leading-normal mb-1">Invoice Number: {datas.invoiceNumber}</p>
            <p className="flex text-black text-sm font-bold gap-2 leading-normal">Customer: {requisitionData[0]?.customer?.businessName}</p>
          </div>
          <div className="border-black border-l-2 w-[50%] mb-2 pl-8 pt-3">
            <p className="flex text-black text-sm font-bold gap-2 leading-normal mb-1"><span>INVOICE DATE: </span><span>{createAtDateOnly}</span></p>
            <p className="flex text-black text-sm font-bold gap-2 leading-normal mb-1"><span>Loading slip NO: </span><span>{datas.loadingSlip.loadingNumber}</span></p>
            <p className="flex text-black text-sm font-bold gap-2 leading-normal mb-1"><span>APPROVAL NO: </span><span>{approvalNumbers}</span></p>
          </div>
        </div>
        <div className="flex items-center mt-0 pt-0">
          <div className="w-full mb-1">
            <p className="flex text-black text-sm font-bold gap-2 leading-normal mb-1">
              <span>Supply Location: </span>
              <span><strong>{datas.loadingSlip.supplyLocation}</strong></span>
            </p>
          </div>

        </div>
        <div className="w-full mb-1">
          <p className="flex text-black text-sm font-bold gap-1">
            <span>Destination:</span>
            <p>{datas.loadingSlip.destination}</p>
          </p>
        </div>
        <div className="w-full mb-1">
          <p className="flex text-black text-md font-bold gap-2 mb-1">
            <span>TRUCK NO:</span>
            <span>{datas.loadingSlip.truckNumber}</span>
          </p>
        </div>
        <table className="w-full">
          <thead>
            <tr>
              <th className="border-b border-black text-black text-left text-lg font-bold leading-normal uppercase">ReqRef</th>
              <th className="border-b border-black text-black text-left text-lg w-[120px] font-bold leading-normal min-w-[120px] uppercase">PRODUCT</th>
              <th className="border-b border-black text-black text-left text-lg font-bold leading-normal uppercase">Quantity</th>
              <th className="border-b border-black text-black text-left text-lg font-bold leading-normal uppercase">U.P.</th>
              <th className="border-b border-black text-black text-left text-lg font-bold leading-normal uppercase">VAT</th>
              <th className="border-b border-black text-black text-left text-lg font-bold leading-normal uppercase">COST</th>
            </tr>
          </thead>
          <tbody>
            <tr className={isMerged ? "merged-row" : ""}>
              <th className="h-9 text-black text-left text-sm font-bold leading-normal uppercase">{approvalNumbers}</th>
              <th className="h-9 text-black text-left text-sm font-bold leading-normal uppercase">
                {requisitionData[0]?.itemName}
                {isMerged && <span className="merged-badge" style={{ fontSize: "8px" }}>Merged</span>}
              </th>
              <th className="border-black border-r h-9 text-black text-left text-sm font-bold leading-normal uppercase">{totalQuantityToLoad}</th>
              <th className="h-9 text-black text-left text-sm font-bold leading-normal pl-2 uppercase">{unitPrice}</th>
              <th className="h-9 text-black text-left text-md font-bold leading-normal uppercase">0.00</th>
              <th className="h-9 text-black text-left text-sm font-bold leading-normal uppercase">{totalAmount}</th>
            </tr>
            <tr className="mb-5">
              <td></td>
              <th className="h-9 text-black text-left text-sm font-bold leading-normal uppercase">TOTAL QTY</th>
              <th className="h-9 text-black text-left text-sm font-bold leading-normal uppercase">{totalQuantityToLoad}</th>
              <td></td>
              <th className="h-9 text-black text-left text-sm font-bold leading-normal uppercase">TOTAL COST ₦</th>
              <th className="h-9 text-black text-left text-sm font-bold leading-normal uppercase">{totalAmount}</th>
            </tr>
          </tbody>
        </table>
        <p className="text-black text-sm w-full capitalize font-bold leading-normal mb-2">{totalAmountInWords} only</p>
        <table className="w-[60%]">
          <thead>
            <tr>
              <th className="text-black text-left text-md font-bold leading-normal uppercase">PAY TERM</th>
              <th className="text-black text-left text-md font-bold leading-normal uppercase">Payment instruments referenced</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th className="text-black text-left text-sm font-bold leading-normal">CASH</th>
            </tr>
          </tbody>
        </table>
        <table className="border-black border-t w-full">
          <thead>
            <tr>
              <th className="text-black text-left text-sm font-bold leading-5 uppercase">LOADER’S NAME</th>
              <th className="text-black text-left text-sm font-bold leading-5 pb-1 uppercase">{datas.loadingSlip.loader}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th className="text-black text-left text-sm font-bold leading-5 pb-1 uppercase">DRIVERS NAME</th>
              <th className="text-black text-left text-sm font-bold leading-5 pb-1 uppercase">{datas.loadingSlip.driver}</th>
              <th className="border-black border-l-2 text-black text-left text-sm font-bold leading-5 pb-1 pl-8 uppercase">METER BEFORE</th>
              <th className="text-black text-left text-sm font-bold leading-5 pb-1 uppercase">{datas.loadingSlip.meterBefore}</th>
            </tr>
            <tr className="pb-8">
              <th className="text-black text-left text-sm font-bold leading-5 pb-1 uppercase">MVT</th>
              <th className="text-black text-left text-sm font-bold leading-normal pb-1 uppercase">
                {datas.loadingSlip.transporter === "SELF COLLECTION"
                  ? "SELF COLLECTION"
                  : datas.loadingSlip.transporter}
              </th>
              <th className="border-black border-l-2 text-black text-left text-sm font-bold leading-5 pb-1 pl-8 uppercase">METER AFTER</th>
              <th className="text-black text-left text-md font-bold leading-normal pb-1 uppercase">{datas.loadingSlip.meterAfter}</th>
            </tr>
            <tr className="pb-8">
              <th className="text-black text-left text-sm font-bold leading-5 pb-1 uppercase">LOADING STARTED</th>
              <th className="text-black text-left text-md font-bold leading-normal pb-1 uppercase">{formattedStartDate}</th>
              <th className="border-black border-l-2 text-black text-left text-sm font-bold leading-5 pb-1 pl-8 uppercase">LOADING COMPLETED</th>
              <th className="text-black text-left text-md font-bold leading-normal pb-1 uppercase">{formattedEndDate}</th>
            </tr>
            <tr>
              <th className="text-black text-left text-sm font-bold leading-5 pb-1 uppercase">Ullage Name</th>
              <th className="text-black text-left text-sm font-bold leading-normal pb-1 uppercase">{datas?.loadingSlip.ullager}</th>
              <th className="border-black border-l-2 text-black text-left text-sm font-bold leading-5 pb-1 pl-8 uppercase">METER Ticket</th>
              <th className="text-black text-left text-md font-bold leading-normal pb-1 uppercase">{datas.meterTicket}</th>
            </tr>
          </tbody>
        </table>
        <table className="border-black border-t w-full">
          <thead>
            <tr>
              <th className="text-black text-left text-sm w-17 font-bold leading-normal pb-2 uppercase">Compartment No</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">1</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">2</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">3</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">4</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">5</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">6</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">7</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th className="text-black text-left text-sm w-17 font-bold leading-normal pb-2 uppercase">Compartment in LTR</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">{datas?.loadingSlip.compartmentNo.first}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">{datas?.loadingSlip.compartmentNo.second}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">{datas?.loadingSlip.compartmentNo.third}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">{datas?.loadingSlip.compartmentNo.fourth}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">{datas?.loadingSlip.compartmentNo.fifth}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">{datas?.loadingSlip.compartmentNo.sixth}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal pb-2 uppercase">{datas?.loadingSlip.compartmentNo.seventh}</th>
            </tr>
            <tr>
              <th className="text-black text-left text-sm w-17 font-bold leading-normal uppercase">Ullage</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal uppercase">{datas?.loadingSlip.ullage.first}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal uppercase">{datas?.loadingSlip.ullage.second}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal uppercase">{datas?.loadingSlip.ullage.third}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal uppercase">{datas?.loadingSlip.ullage.fourth}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal uppercase">{datas?.loadingSlip.ullage.fifth}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal uppercase">{datas?.loadingSlip.ullage.sixth}</th>
              <th className="text-black text-left text-sm w-16 font-bold leading-normal uppercase">{datas?.loadingSlip.ullage.seventh}</th>
            </tr>
          </tbody>
        </table>

        <div className="pb-1">
          <p className="border-black border-t text-black text-sm w-[100%] font-bold mb-0">
            Products loaded in good condition to be confirmed by signing of this document
          </p>
          <div className="flex text-black gap-[5rem] mb-0 mt-5">
            <div>
              <p className="text-sm font-bold">………………….</p>
              <p className="text-sm font-bold mb-2 mt-[-4px]">SIGNATURE</p>
              <p className="text-sm font-bold">………………….</p>
              <p className="text-sm font-bold mb-2 mt-[-4px]">CUSTOMER NAME</p>
              <p className="text-sm font-bold">………………….</p>
              <p className="text-sm font-bold mt-[-4px]">Date & Time</p>
            </div>
            <div className="mr-[5rem] staffSign">
              <p className="text-sm font-bold">………………….</p>
              <p className="text-sm font-bold mb-2 mt-[-4px]">SIGNATURE</p>
              <p className="text-sm font-bold">………………….</p>
              <p className="text-sm font-bold mb-2 mt-[-4px]">STAFF NAME</p>
              <p className="text-sm font-bold">………………….</p>
              <p className="text-sm font-bold mt-[-4px]">Date & Time</p>
            </div>
          </div>
          <div className="flex flex-col float-right gap-0 ml-[-28rem] mt-[-5rem]">
            <p className="text-black text-xs font-bold mb-1 pb-0">Number of times printed: {printCount}</p>
            <p className="text-black text-xs font-bold">Generated by: {datas.loadingSlip.staff.name}</p>
          </div>
        </div>
      </div>
      <div className="flex actionBtns gap-5">
        <button onClick={generatePDF} className="bg-blue-800 border border-blue-800 rounded-md text-sm text-white cursor-pointer ml-4 px-2 py-1">Print Receipt</button>
        <button onClick={sendEmail} className="bg-blue-800 border border-blue-800 rounded-md text-sm text-white cursor-pointer px-2 py-1">
          <AiOutlineShareAlt className="inline mr-1" /> Share
        </button>
        <button onClick={() => navigate(-1)}>Cancel</button>
      </div>
    </div>
  );
};

export default Invoice;